/**
 * TrendsList Component
 * 
 * Displays actualized trend data for celebrities.
 */

import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from '../../../components/ui/pagination';
import { Badge } from '../../../components/ui/badge';
import { Card } from '../../../components/ui/card';
import { Progress } from '../../../components/ui/progress';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../components/ui/tooltip';
import {
  TrendingUp,
  ArrowUp,
  ArrowDown,
  Loader2
} from 'lucide-react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler,
} from 'chart.js';
import { cn } from "@/lib/utils";
import { getTrends, getCelebrityById } from '../lib/apiService';
import CelebrityDetails from './CelebrityDetails';
import { getDomainLabel } from '../../../constants/domains';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend,
  Filler
);

// Component to display trend history in tooltip with chart
const TrendHistoryTooltip = ({ trendHistory, horizon }) => {
  if (!trendHistory || !Array.isArray(trendHistory) || trendHistory.length === 0) {
    return <p className="p-2 text-sm">История тренда недоступна</p>;
  }

  const sortedHistory = [...trendHistory]
    .filter(item => item && typeof item === 'object' && 'value' in item)
    .sort((a, b) => {
      const dateA = a.date ? new Date(a.date) : null;
      const dateB = b.date ? new Date(b.date) : null;
      if (!dateA || !dateB || isNaN(dateA.getTime()) || isNaN(dateB.getTime())) return 0;
      return dateA - dateB;
    });

  if (sortedHistory.length === 0) return <p className="p-2 text-sm">История тренда недоступна</p>;

  // Prepare data for chart
  const labels = sortedHistory.map((item) => {
    if (item.date) {
      try {
        const parsedDate = new Date(item.date);
        if (!isNaN(parsedDate.getTime())) {
          return parsedDate.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
        }
      } catch (e) {
        // Ignore parsing errors for individual dates
      }
    }
    return '';
  });

  const values = sortedHistory.map((item) => {
    return typeof item.value === 'number' ? item.value : (item.value ? parseFloat(item.value) : 0);
  });

  const primaryColor = '#000000'; // Foreground
  const borderColor = '#e5e5e5'; // Border
  const mutedColor = '#a3a3a3'; // Muted

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Оценка тренда',
        data: values,
        borderColor: primaryColor,
        backgroundColor: 'rgba(0,0,0,0.1)',
        borderWidth: 2,
        tension: 0.4,
        fill: true,
        pointRadius: 2,
        pointHoverRadius: 4,
        pointBackgroundColor: primaryColor,
        pointBorderColor: '#fff',
        pointBorderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#fff',
        titleColor: '#000',
        bodyColor: '#000',
        borderColor: borderColor,
        borderWidth: 1,
        padding: 8,
        displayColors: false,
        callbacks: {
          label(context) {
            return `Оценка: ${context.parsed.y.toFixed(1)}`;
          }
        }
      },
    },
    scales: {
      x: {
        grid: { color: 'transparent', drawBorder: false },
        ticks: { color: mutedColor, font: { size: 10 } },
      },
      y: {
        grid: { color: 'transparent', drawBorder: false },
        ticks: { display: false }, // Minimalist
        beginAtZero: false,
      },
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    }
  };

  return (
    <div className="w-[300px] h-[150px] bg-background p-2">
      <p className="text-xs font-bold mb-1 text-foreground">Динамика за {horizon || 90} дней:</p>
      <div className="h-[120px] w-full">
        <Line data={chartData} options={chartOptions} />
      </div>
    </div>
  );
};

const TrendsList = ({ geo, filters, onRefresh: _onRefresh }) => {
  const [trends, setTrends] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [sortBy, setSortBy] = useState('extracted_value');
  const [sortOrder, setSortOrder] = useState('desc');
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedCelebrity, setSelectedCelebrity] = useState(null);

  useEffect(() => {
    loadTrends();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, geo, filters, sortBy, sortOrder]);

  const loadTrends = async () => {
    try {
      setLoading(true);
      const data = await getTrends({
        ...(geo && geo !== 'all' && { geo }),
        page: page + 1,
        page_size: pageSize,
        min_score: filters.min_score,
        has_spike: filters.has_spike,
        domain: filters.domain,
        sort_by: sortBy,
        sort_order: sortOrder,
        horizon_days: filters.horizon || 90,
      });
      setTrends(data.trends || []);
      setTotal(data.pagination?.total || 0);
    } catch (error) {
      console.error('Error loading trends:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setPage(0);
  };

  const handleRowClick = async (trend) => {
    if (!trend.celebrity_id) return;
    try {
      const celebrity = await getCelebrityById(trend.celebrity_id);
      setSelectedCelebrity(celebrity);
      setDetailsOpen(true);
    } catch (error) {
      console.error('Error loading celebrity details:', error);
    }
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedCelebrity(null);
  };

  const SortableHead = ({ field, label }) => {
    const isActive = sortBy === field;
    return (
      <TableHead
        className={cn("cursor-pointer hover:bg-muted/50 transition-colors", isActive && "text-foreground font-bold")}
        onClick={(e) => {
          e.stopPropagation();
          handleSort(field);
        }}
      >
        <div className="flex items-center gap-1">
          {label}
          <div className="flex flex-col">
            <ArrowUp className={cn("h-3 w-3", isActive && sortOrder === 'asc' ? "text-primary" : "text-muted-foreground/30")} />
            <ArrowDown className={cn("h-3 w-3 -mt-1", isActive && sortOrder === 'desc' ? "text-primary" : "text-muted-foreground/30")} />
          </div>
        </div>
      </TableHead>
    );
  };

  const totalPages = Math.ceil(total / pageSize);

  if (loading && trends.length === 0) {
    return (
      <div className="flex justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {loading && <Progress value={33} className="h-1 w-full" />}

      <Card className="border border-border">
        <div className="relative w-full overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <SortableHead field="celebrity_name" label="Имя знаменитости" />
                <SortableHead field="domain" label="Сегмент" />
                <SortableHead field="extracted_value" label="Оценка тренда" />
                <SortableHead field="spike_percentage" label="Всплеск" />
                <SortableHead field="last_update_date" label="Последнее обновление" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {trends.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                    {geo && geo !== 'all'
                      ? 'Актуализированных селебрити по выбранному региону нет. Необходимо запустить Актуализацию'
                      : 'Актуализированных селебрити нет. Необходимо запустить Актуализацию'}
                  </TableCell>
                </TableRow>
              ) : (
                trends.map((trend) => (
                  <TableRow
                    key={trend.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleRowClick(trend)}
                  >
                    <TableCell className="font-medium">
                      {trend.celebrity_name || trend.trend}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-normal border-primary text-primary">
                        {getDomainLabel(trend.domain) || 'Другое'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="inline-flex items-center gap-1 cursor-help">
                              <span className="font-mono font-medium">{trend.extracted_value?.toFixed(1) || '0.0'}</span>
                              {trend.has_spike && (
                                <TrendingUp className="h-3 w-3 text-destructive" />
                              )}
                            </div>
                          </TooltipTrigger>
                          <TooltipContent className="bg-background border border-border p-0" side="right" sideOffset={10}>
                            <TrendHistoryTooltip trendHistory={trend.trend_history} horizon={filters.horizon || 90} />
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </TableCell>
                    <TableCell>
                      {trend.has_spike ? (
                        <Badge variant="destructive" className="bg-destructive/10 text-destructive hover:bg-destructive/20 border-destructive/20">
                          +{trend.spike_percentage?.toFixed(1) || 0}%
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {trend.last_update_date
                        ? new Date(trend.last_update_date).toLocaleDateString()
                        : '-'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                onClick={() => handlePageChange(Math.max(0, page - 1))}
                className={page === 0 ? "pointer-events-none opacity-50" : "cursor-pointer"}
              />
            </PaginationItem>
            <PaginationItem>
              <span className="px-4 text-sm text-muted-foreground">
                Страница {page + 1} из {totalPages}
              </span>
            </PaginationItem>
            <PaginationItem>
              <PaginationNext
                onClick={() => handlePageChange(Math.min(totalPages - 1, page + 1))}
                className={page === totalPages - 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}

      {selectedCelebrity && (
        <CelebrityDetails
          open={detailsOpen}
          celebrity={selectedCelebrity}
          onClose={handleCloseDetails}
        />
      )}
    </div>
  );
};

export default TrendsList;
